/**
 * Composant - BiometricAuthStep
 *
 * PREMIÈRE ÉTAPE du workflow de retrait :
 * - état du lecteur et de la carte (bandeau discret en tête de carte) ;
 * - authentification de l'empreinte du client (zone centrale, un état à la fois) ;
 * - compteur de tentatives et verrouillage temporaire après échecs répétés.
 */

import { useState, useCallback, useEffect } from 'react';
import { ArrowRight, Check, CreditCard, Fingerprint, Loader2, Lock, RefreshCw, ShieldCheck, Usb, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { WithdrawalState } from '../../../domain/aggregates/WithdrawalWorkflow';
import { WITHDRAWAL_THRESHOLDS } from './types';
import './BiometricAuthStep.css';

interface BiometricAuthStepProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workflow: any;
  onCancel?: () => void;
}

type Phase = 'lecteur' | 'carte' | 'pret' | 'lecture' | 'succes' | 'echec' | 'verrouille';

/** « Identiv uTrust 2700 R Smart Card Reader 0=== » → « Identiv uTrust 2700 R » */
function nomLecteur(nom?: string | null) {
  if (!nom) return 'Lecteur de carte';
  return nom.replace(/=+$/, '').replace(/\s*Smart Card Reader\s*\d*\s*$/i, '').trim() || nom;
}

export function BiometricAuthStep({ workflow, onCancel }: BiometricAuthStepProps) {
  const {
    state,
    startBiometricAuth,
    proceedToIdentityVerification,
    loading,
    readerStatus,
    readerName,
    cardDetected,
    initializeReader,
    startCardPolling,
    refreshReaderStatus,
  } = workflow;

  const [authResult, setAuthResult] = useState<{ success?: boolean; clientName?: string } | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTimer, setLockTimer] = useState(0);

  const maxAttempts = WITHDRAWAL_THRESHOLDS.maxBiometricAttempts;
  const remainingAttempts = maxAttempts - attemptCount;

  // Initialiser le lecteur au montage
  useEffect(() => {
    const init = async () => {
      const success = await initializeReader();
      if (success) {
        startCardPolling();
      }
    };
    init();
  }, [initializeReader, startCardPolling]);

  const startLockTimer = useCallback(() => {
    setIsLocked(true);
    setLockTimer(30);
    const interval = setInterval(() => {
      setLockTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsLocked(false);
          setAttemptCount(0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleStartScan = async () => {
    if (isLocked) return;
    setIsScanning(true);
    const result = await startBiometricAuth();
    setAuthResult(result);
    setIsScanning(false);

    if (!result?.success) {
      const newCount = attemptCount + 1;
      setAttemptCount(newCount);
      if (newCount >= maxAttempts) {
        startLockTimer();
      }
    }
  };

  const isPending = state === WithdrawalState.BIOMETRIC_PENDING;
  const isSuccess = state === WithdrawalState.BIOMETRIC_SUCCESS;
  const isFailed = state === WithdrawalState.BIOMETRIC_FAILED;
  const lecteurOk = readerStatus === 'connected';

  const phase: Phase = isLocked
    ? 'verrouille'
    : isSuccess
      ? 'succes'
      : isPending || isScanning
        ? 'lecture'
        : isFailed
          ? 'echec'
          : !lecteurOk
            ? 'lecteur'
            : !cardDetected
              ? 'carte'
              : 'pret';

  const textes: Record<Phase, { titre: string; texte: string }> = {
    lecteur: {
      titre: readerStatus === 'checking' ? 'Recherche du lecteur…' : 'Lecteur non détecté',
      texte:
        readerStatus === 'checking'
          ? 'Connexion au lecteur de carte en cours.'
          : "Branchez le lecteur de carte et vérifiez que l'agent CardFingerAuth est démarré.",
    },
    carte: { titre: 'Insérez la carte du client', texte: 'La carte doit rester dans le lecteur pendant toute la vérification.' },
    pret: { titre: 'Prêt pour la vérification', texte: "Demandez au client de poser son doigt sur le capteur, puis lancez la lecture." },
    lecture: { titre: "Lecture de l'empreinte…", texte: 'Le client garde le doigt posé sur le capteur sans bouger.' },
    succes: { titre: 'Empreinte vérifiée', texte: "L'identité du porteur de la carte est confirmée." },
    echec: { titre: 'Empreinte non reconnue', texte: 'Faites essuyer le doigt, repositionnez-le bien à plat, puis réessayez.' },
    verrouille: { titre: 'Vérification suspendue', texte: 'Trop de tentatives échouées pour cette carte.' },
  };

  const statutLecteur = lecteurOk ? 'ok' : readerStatus === 'checking' ? 'wait' : 'ko';

  return (
    <div className="bio">
      <section className="bio-carte">
        {/* ── État du matériel ── */}
        <div className="bio-materiel">
          <Statut
            ton={statutLecteur}
            icone={<Usb />}
            libelle={lecteurOk ? nomLecteur(readerName) : readerStatus === 'checking' ? 'Recherche du lecteur' : 'Lecteur absent'}
          />
          {lecteurOk && (
            <Statut ton={cardDetected ? 'ok' : 'ko'} icone={<CreditCard />} libelle={cardDetected ? 'Carte insérée' : 'Aucune carte'} />
          )}
          <button
            type="button"
            onClick={refreshReaderStatus}
            disabled={isScanning || isPending}
            className="bio-refresh"
            title="Actualiser l'état du lecteur"
            aria-label="Actualiser l'état du lecteur"
          >
            <RefreshCw className={readerStatus === 'checking' ? 'bio-tourne' : undefined} />
          </button>
        </div>

        {/* ── Zone d'empreinte ── */}
        <div className="bio-corps">
          <ZoneEmpreinte phase={phase} />

          <AnimatePresence mode="wait">
            <motion.div
              key={phase}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
            >
              <h2 className="bio-titre">{textes[phase].titre}</h2>
              <p className="bio-texte">{textes[phase].texte}</p>
            </motion.div>
          </AnimatePresence>

          {(phase === 'echec' || (phase === 'pret' && attemptCount > 0)) && (
            <div className="bio-tentatives" aria-live="polite">
              <div className="bio-barres">
                {Array.from({ length: maxAttempts }).map((_, i) => (
                  <span key={i} className={i < attemptCount ? 'ko' : undefined} />
                ))}
              </div>
              <p className={remainingAttempts <= 1 ? 'derniere' : undefined}>
                {remainingAttempts} tentative{remainingAttempts > 1 ? 's' : ''} restante{remainingAttempts > 1 ? 's' : ''}
              </p>
            </div>
          )}

          {phase === 'verrouille' && (
            <p className="bio-pilule rouge" aria-live="polite">
              Nouvel essai possible dans {lockTimer} s
            </p>
          )}

          {phase === 'succes' && authResult?.clientName && (
            <div className="bio-pilule verte">
              <ShieldCheck />
              <strong>{authResult.clientName}</strong>
            </div>
          )}

          {/* ── Action principale ── */}
          <div className="bio-actions">
            {(phase === 'pret' || phase === 'carte' || phase === 'lecteur') && (
              <button type="button" onClick={handleStartScan} disabled={phase !== 'pret' || loading} className="bio-btn principal">
                <Fingerprint />
                Lancer la lecture d&apos;empreinte
              </button>
            )}

            {phase === 'lecture' && (
              <div className="bio-btn attente" role="status">
                <Loader2 className="bio-tourne" />
                Lecture en cours…
              </div>
            )}

            {phase === 'echec' && (
              <button type="button" onClick={handleStartScan} disabled={loading} className="bio-btn principal">
                <RefreshCw />
                Réessayer
              </button>
            )}

            {phase === 'succes' && (
              <button type="button" onClick={() => proceedToIdentityVerification()} className="bio-btn principal">
                Continuer vers la pièce d&apos;identité
                <ArrowRight />
              </button>
            )}

            {onCancel && (phase === 'echec' || phase === 'verrouille') && remainingAttempts <= 1 && (
              <button type="button" onClick={onCancel} className="bio-btn secondaire">
                Annuler l&apos;opération
              </button>
            )}
          </div>
        </div>

        <p className="bio-pied">
          <Lock />
          L&apos;empreinte est comparée à celle enregistrée sur la puce de la carte. Obligatoire pour tout retrait.
        </p>
      </section>
    </div>
  );
}

/** Pastille d'état (lecteur, carte) */
function Statut({ ton, icone, libelle }: { ton: 'ok' | 'wait' | 'ko'; icone: React.ReactNode; libelle: string }) {
  return (
    <span className={`bio-statut ${ton}`}>
      <span className="pt" />
      {icone}
      <span className="txt">{libelle}</span>
    </span>
  );
}

/** Grand cercle central : une représentation par état */
function ZoneEmpreinte({ phase }: { phase: Phase }) {
  const ton =
    phase === 'succes' ? 'succes' : phase === 'echec' || phase === 'verrouille' ? 'erreur' : phase === 'pret' || phase === 'lecture' ? 'vert' : '';

  return (
    <div className="bio-zone">
      {phase === 'lecture' && (
        <>
          <span className="bio-onde" />
          <span className="bio-anneau" />
        </>
      )}
      {phase === 'pret' && <span className="bio-halo" />}

      <div className={`bio-cercle ${ton}`}>
        <AnimatePresence mode="wait">
          <motion.span
            key={phase === 'lecture' || phase === 'pret' ? 'empreinte' : phase}
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.7, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ display: 'flex' }}
          >
            {phase === 'succes' ? (
              <Check className="moyen" strokeWidth={2.5} />
            ) : phase === 'echec' ? (
              <X className="moyen" strokeWidth={2.5} />
            ) : phase === 'verrouille' ? (
              <Lock className="moyen" />
            ) : phase === 'carte' ? (
              <CreditCard className="moyen" strokeWidth={1.5} />
            ) : phase === 'lecteur' ? (
              <Usb className="moyen" strokeWidth={1.5} />
            ) : (
              <Fingerprint className="grand" strokeWidth={1.25} />
            )}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
}
